import React, { type ComponentProps, useCallback, useEffect } from 'react';
import {
  type RoutePath,
  Tabs,
  useGlobalSearchParams,
  usePathname,
  useRouter,
  useSegments,
} from 'expo-router';
import { useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';
import Animated, {
  Easing,
  FadeIn,
  FadeInLeft,
  FadeOut,
  LinearTransition,
  useReducedMotion,
  useSharedValue,
} from 'react-native-reanimated';

import { Navbar, NavbarBackLink, theme } from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';

import { useBagCount } from '../../../../hooks';
import { useNavigateBack } from '../../../../navigation';
import { MENU_VIEWS, NAVBAR_TAB_NAMES, TABS } from '../../NavbarV2.constant';
import { navbarV2Messages as messages } from '../../NavbarV2.messages';
import { NavbarBagButton } from '../NavbarBagButton';
import { NavbarLink } from '../NavbarLink';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarXS = (props: Props) => {
  const { tabBarProps } = props;

  const router = useRouter();
  const segments = useSegments();
  const currentPath = usePathname();
  const { category, view } = useGlobalSearchParams<MenuSearchParams>();
  const navigateBack = useNavigateBack();
  const isReduceMotionEnabled = useReducedMotion();

  const { formatMessage } = useIntl();
  const { track } = useTelemetry();
  const { bagCount } = useBagCount();

  // ─── Animated Values ────────────────────────────────────────────────

  const activeTabIndexSV = useSharedValue(
    getActiveTabIndex(
      getActiveTabName({
        segments,
        stateActiveTabName: tabBarProps.state.routes[tabBarProps.state.index]?.name,
      }),
    ),
  );

  // ─── Derived ────────────────────────────────────────────────────────

  const stateActiveRoute = tabBarProps.state.routes[tabBarProps.state.index];
  const stateActiveTabName = stateActiveRoute?.name;
  const activeTabName = getActiveTabName({ segments, stateActiveTabName });

  const isHomeTabActive = activeTabName === TABS.HOME;
  const isAccountTabActive = activeTabName === TABS.ACCOUNT;
  const isRewardsTabActive = activeTabName === TABS.REWARDS;
  const isGiftTabActive = activeTabName === TABS.GIFT;
  const isMenuTabActive = activeTabName === TABS.MENU;
  const isProductMenuView = view === MENU_VIEWS.MENU;
  const hasCategoryParam = category !== undefined;

  const isAccountMenuRoute = segments.length === 3 && segments[1] === TABS.ACCOUNT && segments[2] === TABS.MENU_SINGLE; // prettier-ignore
  const isAccountGiftCardConfirmationRoute = currentPath.startsWith(
    ACCOUNT_GIFT_CARD_CONFIRMATION_PATH_PREFIX,
  );
  const isTopLevelTabRoute = segments.length === 2 || isAccountMenuRoute;
  const shouldAnimateNavbar = !isReduceMotionEnabled;
  const shouldRenderCompactNavbar = (segments.length >= 3 && !isTopLevelTabRoute && !isMenuTabActive) || (isMenuTabActive && (hasCategoryParam || isProductMenuView)) || isGiftTabActive || isRewardsTabActive; // prettier-ignore
  const hasActiveBag = bagCount > 0;

  const shouldRenderOrderCta = !hasActiveBag && isTopLevelTabRoute && (isHomeTabActive || isAccountTabActive); // prettier-ignore

  const routes = shouldRenderCompactNavbar
    ? tabBarProps.state.routes.filter(checkIfHomeRoute)
    : tabBarProps.state.routes;
  const navbarRoutes = routes.filter(checkIfNavbarRoute);

  // ─── Handlers ───────────────────────────────────────────────────────

  const handleBackPress = useCallback(() => {
    track('back_selected', { currentPath });

    if (isAccountGiftCardConfirmationRoute) {
      navigateBack();
      return;
    }

    if (isAccountTabActive) {
      router.dismissTo('/account');
      return;
    }

    if (isGiftTabActive && isTopLevelTabRoute) {
      router.replace('/account');
      return;
    }

    if (isMenuTabActive && (hasCategoryParam || isProductMenuView)) {
      router.setParams({ category: undefined, view: MENU_VIEWS.CATEGORIES });
      return;
    }

    navigateBack();
  }, [currentPath, hasCategoryParam, isAccountGiftCardConfirmationRoute, isAccountTabActive, isGiftTabActive, isMenuTabActive, isProductMenuView, isTopLevelTabRoute, navigateBack, router, track]); // prettier-ignore

  const handleHomePress = useCallback(() => {
    track('nav_bar_home_selected');
    navigateToTab({ ...tabBarProps, tabName: TABS.HOME });
  }, [tabBarProps, track]);

  const handleMenuPress = useCallback(() => {
    track('nav_bar_menu_selected');
    navigateToTab({ ...tabBarProps, tabName: TABS.MENU });
  }, [tabBarProps, track]);

  const handleAccountPress = useCallback(() => {
    track('nav_bar_account_selected');
    navigateToTab({ ...tabBarProps, tabName: TABS.ACCOUNT });
  }, [tabBarProps, track]);

  const handleStartOrderPress = useCallback(() => {
    track('nav_bar_start_order_selected');
    navigateToTab({ ...tabBarProps, tabName: TABS.MENU });
  }, [tabBarProps, track]);

  // ─── Effects ────────────────────────────────────────────────────────

  useEffect(() => {
    activeTabIndexSV.value = getActiveTabIndex(activeTabName);
  }, [activeTabIndexSV, activeTabName]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Navbar.Container>
      <Navbar.LinksGroup
        layout={shouldAnimateNavbar ? NAVBAR_LAYOUT_TRANSITION : undefined}
        style={shouldRenderCompactNavbar ? undefined : styles.linksGroup}
      >
        {shouldRenderCompactNavbar ? (
          <Animated.View
            key="back"
            entering={shouldAnimateNavbar ? NAVBAR_BACK_ENTERING : undefined}
            exiting={shouldAnimateNavbar ? NAVBAR_ITEM_EXITING : undefined}
          >
            <NavbarBackLink
              accessibilityLabel={formatMessage(messages.goBack)}
              onPress={handleBackPress}
            />
          </Animated.View>
        ) : undefined}

        {navbarRoutes.map((route) => (
          <Animated.View
            key={`${shouldRenderCompactNavbar ? 'compact' : 'full'}-${route.key}`}
            entering={shouldAnimateNavbar ? NAVBAR_ITEM_ENTERING : undefined}
            exiting={shouldAnimateNavbar ? NAVBAR_ITEM_EXITING : undefined}
            style={shouldRenderCompactNavbar ? styles.linkContainerCompact : styles.linkContainer}
          >
            <NavbarLink
              route={route}
              activeTabIndexSV={activeTabIndexSV}
              isActive={route.name === activeTabName}
              onAccountPress={handleAccountPress}
              onHomePress={handleHomePress}
              onMenuPress={handleMenuPress}
            />
          </Animated.View>
        ))}
      </Navbar.LinksGroup>

      <Navbar.CtaContainer>
        {hasActiveBag ? <NavbarBagButton /> : null}

        {shouldRenderOrderCta ? (
          <Navbar.Button
            title={formatMessage(messages.startOrder)}
            onPress={handleStartOrderPress}
          />
        ) : null}
      </Navbar.CtaContainer>
    </Navbar.Container>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function navigateToTab(params: NavigateToTabParams) {
  const { descriptors, navigation, state, tabName } = params;

  const route = state.routes.find((stateRoute) => stateRoute.name === tabName);

  if (!route) {
    return;
  }

  const event = navigation.emit({ type: 'tabPress', target: route.key, canPreventDefault: true });

  if (event.defaultPrevented) {
    return;
  }

  const options = descriptors[route.key].options as TabOptions;

  options.onPress?.();
  navigation.navigate(tabName, undefined, { merge: true });
}

function checkIfHomeRoute(route: TabRoute) {
  return route.name === TABS.HOME;
}

function checkIfNavbarRoute(route: TabRoute) {
  return NAVBAR_TAB_NAMES.some((tabName) => tabName === route.name);
}

function getActiveTabIndex(routeName?: string) {
  return NAVBAR_TAB_NAMES.findIndex((tabName) => tabName === routeName);
}

function getActiveTabName(params: GetActiveTabNameParams) {
  const { segments, stateActiveTabName } = params;

  if (segments.some((segment) => segment === TABS.ACCOUNT)) {
    return TABS.ACCOUNT;
  }

  if (segments.some((segment) => segment === TABS.MENU || segment === TABS.MENU_SINGLE)) {
    return TABS.MENU;
  }

  if (segments.some((segment) => segment === TABS.REWARDS)) {
    return TABS.REWARDS;
  }

  if (segments.some((segment) => segment === TABS.GIFT)) {
    return TABS.GIFT;
  }

  if (segments.some((segment) => segment === TABS.HOME || segment === TABS.HOME_SINGLE)) {
    return TABS.HOME;
  }

  return stateActiveTabName;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const ACCOUNT_GIFT_CARD_CONFIRMATION_ROUTE =
  '/account/gift-cards/confirmation/[orderId]' satisfies RoutePath;
const ACCOUNT_GIFT_CARD_CONFIRMATION_PATH_PREFIX = ACCOUNT_GIFT_CARD_CONFIRMATION_ROUTE.replace(
  '[orderId]',
  '',
);

const NAVBAR_LAYOUT_TRANSITION = LinearTransition.duration(theme.transitions.long).easing(
  Easing.bezier(0.2, 0, 0, 1),
);

const NAVBAR_BACK_ENTERING = FadeInLeft.duration(theme.transitions.base);
const NAVBAR_ITEM_ENTERING = FadeIn.duration(theme.transitions.base);
const NAVBAR_ITEM_EXITING = FadeOut.duration(theme.transitions.base);

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  linksGroup: {
    flex: 1,
    maxWidth: 320,
  },
  linkContainerCompact: {
    minWidth: 60,
  },
  linkContainer: {
    flexBasis: 0,
    minWidth: 60,
    flexGrow: 1,
    flexShrink: 1,
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type Props = {
  tabBarProps: TabBarProps;
};

type TabBarProps = Parameters<NonNullable<ComponentProps<typeof Tabs>['tabBar']>>[0];
type TabRoute = TabBarProps['state']['routes'][number];
type TabOptions = { onPress?: () => void };

type NavigateToTabParams = TabBarProps & { tabName: NavbarTabName };
type NavbarTabName = typeof TABS.HOME | typeof TABS.MENU | typeof TABS.ACCOUNT;
type GetActiveTabNameParams = { segments: readonly string[]; stateActiveTabName?: string };
type MenuView = (typeof MENU_VIEWS)[keyof typeof MENU_VIEWS];
type MenuSearchParams = { category?: string | string[]; view?: MenuView | MenuView[] };
